/**
 * schools-list.ts
 *
 * Handles the AJAX behaviour for the Schools list page:
 * - 300ms debounce on the search input (#searchTerm)
 * - Immediate fetch on the status select change
 * - Cancels in-flight requests using AbortController when a new input arrives
 * - Updates #schoolsList innerHTML with the returned partial view HTML
 * - Keeps the browser URL in sync with window.history.replaceState
 */

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let activeController: AbortController | null = null;

/**
 * Fetches the school list partial from the server and replaces the #schoolsList div.
 * @param url - The request URL including query-string parameters.
 */
async function fetchSchoolList(url: string): Promise<void> {
  // Cancel any previous in-flight request
  if (activeController) {
    activeController.abort();
  }

  activeController = new AbortController();

  const listEl = document.getElementById('schoolsList');
  if (!listEl) return;

  try {
    const response = await fetch(url, {
      signal: activeController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      // Non-2xx responses are unexpected; leave the current list intact
      console.error(`School list fetch failed: ${response.status}`);
      return;
    }

    const html = await response.text();
    listEl.innerHTML = html;

    // Sync the browser address bar without a page reload
    window.history.replaceState(null, '', url);
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Request was intentionally cancelled — no action needed
      return;
    }
    console.error('Unexpected error loading school list:', err);
  }
}

/**
 * Builds a URL for the school list with the current filter values.
 */
function buildListUrl(): string {
  const listEl = document.getElementById('schoolsList');
  const baseUrl = listEl?.dataset['listUrl'] ?? window.location.pathname;

  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;

  const params = new URLSearchParams();
  const search = searchInput?.value.trim() ?? '';
  const status = statusSelect?.value ?? '';

  if (search) params.set('searchTerm', search);
  if (status) params.set('status', status);

  // Always reset to page 1 when a filter changes
  params.set('pageNumber', '1');

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Returns true when at least one filter input has a non-empty value.
 */
function hasActiveFilters(): boolean {
  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;
  return (searchInput?.value.trim() ?? '') !== '' || (statusSelect?.value ?? '') !== '';
}

/**
 * Shows or hides the clear-filters button based on whether any filter is active.
 * @param hasFilters - true when at least one filter has a non-empty value.
 */
function updateClearButton(hasFilters: boolean): void {
  const container = document.getElementById('clearFiltersContainer');
  if (!container) return;
  container.classList.toggle('d-none', !hasFilters);
}

/**
 * Initialises the schools list AJAX behaviour.
 * Call once after the DOM is ready.
 */
export function initSchoolsList(): void {
  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        const url = buildListUrl();
        updateClearButton(hasActiveFilters());
        void fetchSchoolList(url);
      }, 300);
    });
  }

  if (statusSelect) {
    statusSelect.addEventListener('change', () => {
      updateClearButton(hasActiveFilters());
      void fetchSchoolList(buildListUrl());
    });
  }
}
